<template>
  <div class="d-flex gap-3">
    <div v-if="readonly">
      <label class="form-label">{{ $t('additional_discount') }}</label>
      <div>{{ additional_discount || 0 }} {{ postFix }}</div>
    </div>
    <div v-if="!insuranceCompany && !readonly" class="w-100">
      <InputGroup type="text"
                  ref="discountInput"
                  :renderResult="inputFormatter"
                  :readonly="(forVisit && visit.is_paid == '1') || !rights.includes('edit_discounts') || cannotChange"
                  :label="$t('additional_discount')"
                  validationId="discountinput"
                  formLabelClass="force-one-line-label"
                  :key="`discount-key-${amountKey}`"
                  :required="true"
                  v-model="additional_discount"/>
    </div>
    <div v-if="!insuranceCompany && !readonly">
      <div class="form-group">
        <label class="form-label">{{ $t('type') }}&nbsp;&nbsp;</label>
        <select style="width: 70px"
                :disabled="!rights.includes('edit_discounts') || cannotChange"
                class="form-control"
                v-model="rateType">
          <option value="absolute">{{ getCurrencyName }}</option>
          <option value="percent">%</option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup>
import InputGroup from "@/components/form/InputGroup.vue"
import {computed, onMounted, ref, watch} from "vue"
import {useStore} from "vuex"
import {numberInputFormatter, sanitizeNumberInput} from "@/services/number.service"

const store = useStore()

const props = defineProps({
  forVisit: {
    type: Boolean,
    default: true
  },
  readonly: {
    type: Boolean,
    default: false
  },
  cannotChange: {
    type: Boolean,
    default: false
  },
  costOfMaterialsForSale: {
    type: Number,
  }
})

const emit = defineEmits(["discountUpdated"])

const rights = computed(() => store.state.auth.rights)
const getCurrencyName = computed(() => store.getters.getCurrencyName)
const visit = computed(() => store.state.patient.visit)
const insuranceCompany = computed(() => visit.value?.insurance_company)
const visitCostServicesWithDiscount = computed(() => props.forVisit ? store.getters.visitCostServicesWithDiscount : props.costOfMaterialsForSale)
const counter = computed(() => store.state.datatable.dataTableCounter)
const discountAbsoluteSum = computed(() => {
  let percent = additional_discount.value
  if(typeof percent === 'string') {
    percent = parseFloat(percent.replaceAll(' ', ''))
    if(isNaN(percent)) percent = 0
  }
  return rateType.value === 'absolute' ? percent : (Math.round(percent * visitCostServicesWithDiscount.value) / 100)
})
const postFix = computed(() => rateType.value === 'absolute' ? getCurrencyName.value : '%')

const rateType = ref('percent')
const amountKey = ref(1)
const additional_discount = ref(null)
const discountInput = ref(null)

const inputFormatter = (val) => {
  val = numberInputFormatter(sanitizeNumberInput(val))
  let p = val
  if(typeof p === 'string') {
    p = parseFloat(p.replaceAll(' ', ''))
  }
  if(p > 100 && rateType.value === 'percent') {
    val = 100
  }
  if(rateType.value === 'absolute' && p > visitCostServicesWithDiscount.value) {
    val = visitCostServicesWithDiscount.value
  }
  if(p < 0) {
    val = null
  }
  if(val === additional_discount.value) {
    amountKey.value++
    setTimeout(() => discountInput.value?.focus(), 10)
  }
  return val
}

onMounted(() => {
  fetchDiscount()
})

const fetchDiscount = () => {
  if(!visit.value) return
  if(visit.value.additional_discount_percent) {
    rateType.value = 'percent'
    additional_discount.value = visit.value.additional_discount_percent
  } else {
    if(visit.value.additional_discount) {
      rateType.value = 'absolute'
      additional_discount.value = visit.value.additional_discount
    }
  }
  if(discountAbsoluteSum.value === 0) {
    additional_discount.value = 0
  }
}

watch(discountAbsoluteSum, (val) => {
  const percent = rateType.value === 'percent' ? additional_discount.value : null
  if(visit.value) {
    visit.value.additional_discount = val
    visit.value.additional_discount_percent = percent
  }
  emit('discountUpdated', {
    absolute: val,
    percent,
  })
})

watch(rateType, (val) => {
  if(val === 'percent') {
    if(additional_discount.value > 100) additional_discount.value = 100
  }
})

watch(counter, () => {
  fetchDiscount()
})
</script>
